import clsx from 'clsx';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary
} from '../../../shared/components/Accordion/Accordion';
import { capitalise } from '../../../shared/utils/string/formatter';
import styles from '../Preferences.module.scss';
import { Checkbox } from '../../../checkbox/Checkbox';
import { Preference, ChannelMeta, SeverityType } from '../Interface';

export interface SeverityGroupProps {
  className: string;
  severity: SeverityType;
  alerts: Preference[];
  channelsMeta: ChannelMeta[];
  onChannelPreferenceForSeverityChange: (
    alertsSet: Set<string>,
    channelType: string,
    selected: boolean
  ) => void;
  onChannelPreferenceForAlertChange: (
    alertType: string,
    channelType: string,
    selected: boolean
  ) => void;
}

export function SeverityGroup({
  className,
  severity,
  alerts,
  channelsMeta,
  onChannelPreferenceForSeverityChange,
  onChannelPreferenceForAlertChange
}: SeverityGroupProps) {
  const allAlertsSelectedForChannel = (channelType: string) => {
    return alerts.every(alert => !!alert.configs[channelType as keyof typeof alert.configs]);
  };

  const indeterminateState = (channelType: string) => {
    return (
      alerts.some(alert => !!alert.configs[channelType as keyof typeof alert.configs]) &&
      !alerts.every(alert => !!alert.configs[channelType as keyof typeof alert.configs])
    );
  };

  return (
    <Accordion
      data-id={`${severity}-severity-group`}
      defaultExpanded
      expanded
      className={clsx('pt-0 bg-surface', className)}
      showBorderBottom
    >
      <AccordionSummary
        className={clsx('bg-surface pl-1 border-bottom', styles.headerBorderTop)}
        expandIcon=''
      >
        <div className='d-flex justify-between w-100'>
          <div className='text-body-2'>{capitalise(severity.toLowerCase())}</div>

          <div className='d-flex'>
            {channelsMeta.map(channel => {
              return (
                <div key={`${severity}-${channel.name}`} className={styles.channelCell}>
                  <Checkbox
                    dataId={`${severity}-${channel.name}`}
                    checked={allAlertsSelectedForChannel(channel.value)}
                    disabled={!channel.enabled}
                    indeterminate={indeterminateState(channel.value)}
                    onChange={(event, checked) => {
                      event.stopPropagation();
                      onChannelPreferenceForSeverityChange(
                        new Set(alerts.map(alert => alert.type)),
                        channel.value,
                        checked
                      );
                    }}
                    onClick={event => {
                      event.stopPropagation();
                    }}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </AccordionSummary>

      <AccordionDetails className='bg-surface px-5 py-0'>
        <>
          {alerts.map((alert, index) => {
            return (
              <div
                key={alert.type}
                className={clsx('w-100 d-flex justify-between py-3', {
                  'border-bottom': index !== alerts.length - 1
                })}
              >
                <div>
                  <div className='text-body-2'>{alert.name}</div>
                  <div className='text-body-1 text-secondary mt-1'>{alert.description}</div>
                </div>

                <div className='d-flex '>
                  {channelsMeta.map(channel => (
                    <div key={`${channel.name}-${alert.type}`} className={styles.channelCell}>
                      <Checkbox
                        key={`${channel.name}-${alert.type}-checkbox`}
                        dataId={`${channel.name}-${alert.type}`}
                        checked={!!alert.configs[channel.value as keyof typeof alert.configs]}
                        disabled={!channel.enabled}
                        onChange={(event, checked) => {
                          event.stopPropagation();
                          onChannelPreferenceForAlertChange(alert.type, channel.value, checked);
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </>
      </AccordionDetails>
    </Accordion>
  );
}
